import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  InvalidTemplateContents,
  InAppContentArgsInput,
  UpsertTemplateResult,
} from 'modules/graphql/types';
import { isEmpty } from '@iterable/utils';
import { addNegativeToast } from 'modules/toastContainer';
import noOp from 'utils/noop';
import {
  GqlInvalidContentReason,
  GqlUpsertTemplateFailureTypes,
  GqlUpsertTemplateInvalidContentFailureTypes,
} from '../../common/types';
import { getErrorMessage } from '../../common/utils';
import { Config } from './types';

export type UpdateInAppTemplateRequest = {
  cacheDataFeed: boolean;
  campaignId?: number;
  content: InAppContentArgsInput;
  dateFeedIds: number[];
  // destinationFolderId?: number;
  mergeDataFeedContext: boolean;
  messageTypeId: number;
  name: string;
  referenceDataFeedsByAlias: boolean;
  templateId: number;
};

type UpdateInAppTemplateResponse = {
  updateInAppTemplate: UpsertTemplateResult;
};

export const UpdateInAppTemplateMutation = gql`
  mutation UpdateInAppTemplate(
    $cacheDataFeed: Boolean!
    $campaignId: Long
    $content: InAppContentArgsInput!
    $dataFeedIds: [Long!]!
    $mergeDataFeedContext: Boolean!
    $messageTypeId: Long!
    $name: String!
    $referenceDataFeedsByAlias: Boolean!
    $templateId: Long!
  ) {
    updateInAppTemplate(
      cacheDataFeed: $cacheDataFeed
      campaignId: $campaignId
      content: $content
      dataFeedIds: $dataFeedIds
      mergeDataFeedContext: $mergeDataFeedContext
      messageTypeId: $messageTypeId
      name: $name
      referenceDataFeedsByAlias: $referenceDataFeedsByAlias
      templateId: $templateId
    ) {
      failure {
        ... on InvalidTemplateContents {
          errors {
            ... on InvalidTemplateContent {
              reasons {
                ... on Unrenderable {
                  fieldName
                  msg
                }
                ... on MissingSnippets {
                  snippets
                }
                ... on InvalidSnippetParams {
                  snippetsWithParams
                }
              }
              content {
                ... on InAppContentArgs {
                  locale {
                    name
                  }
                }
              }
            }
          }
        }
        ... on TemplateNotFound {
          templateId
        }
        __typename
      }
      templateId
    }
  }
`;

export const useUpdateInAppTemplate = ({
  onComplete = noOp,
  onError = noOp,
  onSuccess = noOp,
}: Config) => {
  const dispatch = useDispatch();
  const [upsertInAppTemplate, data] = useMutation<
    UpdateInAppTemplateResponse,
    UpdateInAppTemplateRequest
  >(UpdateInAppTemplateMutation);

  useEffect(() => {
    if (!data.called) return;
    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.updateInAppTemplate) {
      const { failure, templateId } = data.data.updateInAppTemplate;
      const failures = (Array.isArray(failure) || failure === null ? failure : [failure]) ?? [];
      const errorMessages: string[] = [];
      const fieldErrors: Record<string, string> = {};
      failures.forEach((error) => {
        if (error.__typename === GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_CONTENTS) {
          (error as InvalidTemplateContents).errors.forEach((invalidContentError) =>
            invalidContentError.reasons.forEach((reason) => {
              const invalidContentReason = reason as GqlInvalidContentReason;
              if (
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.ILLEGAL_SCRIPT ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_ANCHOR_TAG ||
                invalidContentReason.__typename ===
                  GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_IFRAME_TAG
              )
                errorMessages.push(getErrorMessage(invalidContentReason));
            }),
          );
        } else {
          errorMessages.push(getErrorMessage(error));
        }
      });

      errorMessages.forEach((errorMessage) => dispatch(addNegativeToast(errorMessage)));

      if (templateId) {
        onSuccess(templateId);
      } else if (!isEmpty(fieldErrors)) {
        onError(fieldErrors);
      }
    }
    onComplete();
  }, [data.loading]);
  return [upsertInAppTemplate, data] as const;
};
